// src/pages/Home.jsx
import React, { useState, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Container, Button, Box, Typography, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";

import useGsapReveal from "../hooks/useGsapReveal";
import "../styles/Home.css";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

import {
  Rocket,
  Wallet,
  Handshake,
  Bot,
  BarChart3,
  Vote,
  CircleDollarSign,
  Globe2,
} from "lucide-react";

import partner1 from "../assets/images/partner1.png";
import partner2 from "../assets/images/partner2.png";
import partner3 from "../assets/images/partner3.png";
import partner4 from "../assets/images/partner4.png";
import partner5 from "../assets/images/partner5.png";

// можно оставить, используются как fallback / для других секций при желании
// import tradeAIImage from "../assets/images/trade-ai.png";
// import promoImage from "../assets/images/tokenization.png";
// import promoStart from "../assets/images/promo start.png";

import cryptoPreview from "../assets/images/Crypto.mp4";
import financePreview from "../assets/images/Finance.mp4";
import politicsPreview from "../assets/images/Politics.mp4";
import sportsPreview from "../assets/images/Sports.mp4";

import { useTheme, styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

/* ======================= PALETTE ======================= */
// ориентируемся на рамку логотипа: фиолетовый + синий
const NEON = {
  violet: "#c084fc",
  violetSoft: "rgba(192,132,252,0.65)",
  blue: "#38bdf8",
  blueSoft: "rgba(56,189,248,0.65)",
};

const GLASS = {
  bg: "linear-gradient(135deg, rgba(17,24,39,0.96) 0%, rgba(12,18,30,0.98) 100%)",
  surface:
    "linear-gradient(180deg, rgba(22,27,40,0.98) 0%, rgba(10,14,24,0.99) 78%)",
  border: "1px solid rgba(148,163,184,.38)",
  borderHover: "1px solid rgba(192,132,252,.9)",
  shadow: "0 18px 50px rgba(2,8,23,.65)",
  text: "#e5e7eb",
  subtext: "rgba(148,163,184,.95)",
  brandGrad: `linear-gradient(90deg, ${NEON.violet} 0%, ${NEON.blue} 100%)`,
};

const partners = [partner1, partner2, partner3, partner4, partner5];

const videoPreviews = [
  cryptoPreview,
  financePreview,
  politicsPreview,
  sportsPreview,
];

/* ================= Styled ================= */

const StepBox = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 16,
  marginBottom: 16,
});

const StepIcon = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: GLASS.brandGrad,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  flexShrink: 0,
  boxShadow:
    "0 8px 22px rgba(192,132,252,.35), 0 6px 20px rgba(56,189,248,.35)",
  border: "1px solid rgba(255,255,255,.22)",
});

const StepText = styled(Box)({ width: "100%" });

const HeroSection = styled(Box)({
  position: "relative",
  width: "100%",
  background: "transparent",
  padding: "48px 32px",
  boxSizing: "border-box",
  overflow: "hidden",
  "@media (max-width: 600px)": { padding: "24px 16px" },
});

const MarqueeWrapper = styled(Box)({
  position: "relative",
  background: GLASS.bg,
  color: GLASS.text,
  padding: "16px 24px",
  marginBottom: 32,
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 16,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  backdropFilter: "blur(10px) saturate(120%)",
  WebkitBackdropFilter: "blur(10px) saturate(120%)",
  overflow: "hidden",
  "::before, ::after": {
    content: '""',
    position: "absolute",
    top: 0,
    width: 110,
    height: "100%",
    zIndex: 2,
    pointerEvents: "none",
  },
  "::before": {
    left: 0,
    background:
      "linear-gradient(90deg, rgba(15,23,42,1) 0%, rgba(15,23,42,0) 100%)",
  },
  "::after": {
    right: 0,
    background:
      "linear-gradient(270deg, rgba(15,23,42,1) 0%, rgba(15,23,42,0) 100%)",
  },
});

const SloganBox = styled(Box)({
  textAlign: "center",
  maxWidth: 1100,
  margin: "0 auto",
  padding: 32,
  width: "100%",
  position: "relative",
  zIndex: 1,
  boxSizing: "border-box",
  color: GLASS.text,
  "@media (max-width: 600px)": { padding: 16 },
});

const SloganTitle = styled(Typography)({
  background: GLASS.brandGrad,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 800,
});

const FeatureList = styled("ul")(({ theme }) => ({
  maxWidth: 980,
  margin: "24px auto 0",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gap: 14,
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("md")]: {
    gap: 16,
    gridTemplateColumns: "1fr 1fr",
  },
  "& li": {
    display: "grid",
    gridTemplateColumns: "18px 1fr",
    alignItems: "center",
    columnGap: 12,
    padding: "12px 14px",
    borderRadius: 12,
    background: "rgba(15,23,42,.9)",
    backdropFilter: "blur(6px) saturate(120%)",
    WebkitBackdropFilter: "blur(6px) saturate(120%)",
    border: "1px solid rgba(148,163,184,.4)",
    boxShadow: "0 6px 18px rgba(2,8,23,.45)",
    color: GLASS.text,
  },
  "& li::before": {
    content: '""',
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: GLASS.brandGrad,
    boxShadow:
      "0 0 0 3px rgba(192,132,252,.28), 0 6px 14px rgba(15,23,42,.7)",
    display: "inline-block",
  },
  "& span": {
    color: `${GLASS.text} !important`,
    fontFamily: "'Bruno Ace SC', sans-serif",
    fontWeight: 600,
    letterSpacing: ".2px",
    fontSize: "1.05rem",
    lineHeight: 1.35,
    textShadow: "0 1px 0 rgba(0,0,0,.45)",
  },
  "@media (max-width: 600px)": {
    "& span": { fontSize: "1rem" },
  },
}));

const FeatureBlock = styled(Box)(({ theme }) => ({
  background: GLASS.bg,
  backdropFilter: "blur(10px) saturate(120%)",
  WebkitBackdropFilter: "blur(10px) saturate(120%)",
  borderRadius: 20,
  padding: 40,
  boxShadow: GLASS.shadow,
  border: GLASS.border,
  transition:
    "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
  position: "relative",
  zIndex: 1,
  boxSizing: "border-box",
  color: GLASS.text,

  width: "100%",
  maxWidth: theme.breakpoints.values.xl, // центрируем по контейнеру
  margin: "0 auto",

  "&:hover": {
    transform: "translateY(-4px)", // только по Y
    boxShadow: "0 24px 60px rgba(2,8,23,.75)",
    border: GLASS.borderHover,
  },

  "@media (max-width: 960px)": {
    flexDirection: "column",
    textAlign: "center",
    padding: 32,
  },
  "@media (max-width: 600px)": { padding: 24 },
}));


const ModelCard = styled(Box)({
  background: GLASS.surface,
  borderRadius: 16,
  padding: 28,
  boxShadow: GLASS.shadow,
  border: GLASS.border,
  transition:
    "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
  width: "100%",
  color: GLASS.text,
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 18px 44px rgba(2,8,23,.7)",
    border: GLASS.borderHover,
  },
  "@media (max-width: 960px)": { padding: 24 },
  "@media (max-width: 600px)": { padding: 18 },
});

const AnimatedButton = styled(Button)({
  padding: "14px 28px",
  fontSize: "1.1rem",
  fontFamily: "'Bruno Ace SC', sans-serif",
  background: GLASS.brandGrad,
  borderRadius: 12,
  textTransform: "uppercase",
  boxShadow: "0 12px 28px rgba(2,8,23,.55)",
  position: "relative",
  overflow: "hidden",
  color: "#020617",
  "&:hover": {
    boxShadow: "0 18px 40px rgba(2,8,23,.85)",
    transform: "translateY(-2px)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.38), transparent)",
    transition: "0.6s",
  },
  "&:hover::before": { left: "100%" },
  "@media (max-width: 600px)": { padding: "10px 20px", fontSize: "1rem" },
});

const glassSectionSX = {
  position: "relative",
  overflow: "hidden",
  background: GLASS.bg,
  backdropFilter: "blur(10px) saturate(120%)",
  WebkitBackdropFilter: "blur(10px) saturate(120%)",
  borderRadius: 14,
  border: GLASS.border,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
};

const ContentPanel = styled(Box)({
  background: GLASS.surface,
  border: GLASS.border,
  borderRadius: 16,
  padding: 24,
  boxShadow: GLASS.shadow,
  color: GLASS.text,
});

const Pill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 10px",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,.55)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,.06), rgba(15,23,42,0.9))",
  fontSize: 12,
  color: GLASS.text,
  fontWeight: 700,
});

const HeroGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.35fr) minmax(0, 1fr)",
  gap: 36,
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: 28,
  },
}));

/* ===== 3D CUBE ===== */

const CubeWrapper = styled(Box)(() => ({
  position: "relative",
  margin: "0 auto",
  perspective: 1200,
}));

const CubeInner = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  height: "100%",
  transformStyle: "preserve-3d",
  animation: "cubeRotate 26s linear infinite",
  "@keyframes cubeRotate": {
    "0%": { transform: "rotateX(-18deg) rotateY(0deg)" },
    "50%": { transform: "rotateX(18deg) rotateY(180deg)" },
    "100%": { transform: "rotateX(-18deg) rotateY(360deg)" },
  },
  "&:hover": {
    animationPlayState: "paused",
  },
}));

const CubeFace = styled(Box)(() => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 24,
  overflow: "hidden",
  background:
    "radial-gradient(circle at 0% 0%, rgba(192,132,252,.28), transparent 55%), radial-gradient(circle at 100% 100%, rgba(56,189,248,.28), transparent 55%), rgba(15,23,42, 0.98)",
  border: "1px solid rgba(148,163,184,.6)",
  boxShadow:
    "0 24px 60px rgba(2,8,23,.95), 0 0 0 1px rgba(15,23,42,1), inset 0 0 40px rgba(15,23,42,.85)",
}));

const CubeHalo = styled(Box)(() => ({
  position: "absolute",
  inset: "14%",
  borderRadius: "32px",
  background:
    "radial-gradient(circle at 0% 0%, rgba(192,132,252,.35), transparent 70%), radial-gradient(circle at 100% 100%, rgba(56,189,248,.35), transparent 70%)",
  filter: "blur(18px)",
  opacity: 0.9,
  pointerEvents: "none",
}));


const PartnerBadge = styled(Box)(() => ({
  position: "relative",
  width: 120,
  height: 120,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const PartnerHalo = styled(Box)(() => ({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  background:
    "radial-gradient(circle at 30% 0%, rgba(167,139,250,0.70), transparent 60%)," +
    "radial-gradient(circle at 70% 120%, rgba(56,189,248,0.55), transparent 65%)",
  boxShadow:
    "0 0 26px rgba(167,139,250,0.9), 0 18px 40px rgba(15,23,42,0.9)",
  opacity: 0.95,
}));

const PartnerCircle = styled(Box)(() => ({
  position: "relative",
  width: "88%",          // было 78%
  height: "88%",
  borderRadius: "50%",
  background:
    "radial-gradient(circle at 20% 0%, rgba(15,23,42,0.98), rgba(2,6,23,1))",
  border: "1px solid rgba(199,210,254,0.8)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
}));

const PartnerLogo = styled("img")(() => ({
  width: "72%",          // было 68%
  height: "72%",
  objectFit: "contain",
  filter: "grayscale(1) contrast(1.25) brightness(2.1)",
  opacity: 0.96,
}));


const PartnersRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(4),
}));

// обновлённый бейдж под статичный ряд (без больших внешних отступов)
const PartnersSection = styled(Box)(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(3),
  overflow: "visible",
  background:
    "radial-gradient(900px 400px at 50% 0%, rgba(148,163,184,0.12), transparent 70%)",
}));


/* ================= MINOR COMPONENTS ================= */

const VideoCube = memo(function VideoCube({ previews }) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const size = isSmDown ? 260 : 320;
  const depth = size / 2.2;

  const faces = (previews && previews.length
    ? previews
    : videoPreviews
  ).slice(0, 4);

  return (
    <CubeWrapper sx={{ width: size, height: size }}>
      <CubeInner>
        {faces.map((src, idx) => {
          let transform;
          if (idx === 0) transform = `rotateY(0deg) translateZ(${depth}px)`;
          else if (idx === 1) transform = `rotateY(90deg) translateZ(${depth}px)`;
          else if (idx === 2)
            transform = `rotateY(180deg) translateZ(${depth}px)`;
          else transform = `rotateY(-90deg) translateZ(${depth}px)`;

          return (
            <CubeFace key={idx} sx={{ transform }}>
              <video
                src={src}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transform: "scale(1.04)",
                }}
              />
            </CubeFace>
          );
        })}
      </CubeInner>
      <CubeHalo />
    </CubeWrapper>
  );
});

/* ================= Component ================= */

const Home = () => {
  useGsapReveal(".reveal");

  const [activeIndex, setActiveIndex] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const toggleFAQ = (i) => setOpenFAQ(openFAQ === i ? null : i);

  const handleLaunchApp = () => {
    window.location.href = "https://app.promospay.ai";
  };

  const stages = [
    {
      title: "Stage 1-Done",
      desc: "Beta Launch and AI Integration",
      popupDesc:
        "Initial deployment of SCAI V1 with rapid trade data analysis feature.",
      icon: <Rocket size={32} color="#ffffff" />,
    },
    {
      title: "Stage 2-Done",
      desc: "Tokenized reward system implemented",
      popupDesc: "Mechanism for distributing rewards to users.",
      icon: <Wallet size={32} color="#ffffff" />,
    },
    {
      title: "Stage 3-Done",
      desc: "AI advisor (ScAi) goes live",
      popupDesc:
        "Launch of the AI-driven ScAi advisor for real-time insights.",
      icon: <Bot size={32} color="#ffffff" />,
    },
    {
      title: "Stage 4-Done",
      desc: "Launch of the platform for users",
      popupDesc: "Obtaining market insights from our models.",
      icon: <Handshake size={32} color="#ffffff" />,
    },
    {
      title: "Stage 5-Done",
      desc: "Launch Stream market",
      popupDesc:
        "Streaming platforms with analytics features have been launched.",
      icon: <BarChart3 size={32} color="#ffffff" />,
    },
    {
      title: "Stage 6-Done",
      desc: "Launching Predictions",
      popupDesc:
        "Launching Predictions, monetize trading and analytics experience.",
      icon: <Vote size={32} color="#ffffff" />,
    },
    {
      title: "Stage 7",
      desc: "The ability to create your own models and Predictions",
      popupDesc: "Launch of the AI ​​presenter designer.",
      icon: <CircleDollarSign size={32} color="#ffffff" />,
    },
    {
      title: "Stage 8",
      desc: "Buying an AI model and using it in a crypto wallet",
      popupDesc: "Personal AI models with all the data in your crypto wallet coming soon!",
      icon: <Globe2 size={32} color="#ffffff" />,
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 15, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };
  const hoverVariants = {
    rest: { y: 0 },
    hover: { y: -10, transition: { duration: 0.3, ease: "easeOut" } },
  };

  return (
    <div className="home-container" style={{ position: "relative" }}>
   <Box
  sx={{
    position: "relative",
    zIndex: 1,
    minHeight: "100vh",
    pb: 10,
    overflowX: "hidden",
    // фон убираем – используем глобальный
  }}
>

        {/* Hero Section */}
        <HeroSection>
          <Container
            maxWidth="xl"
            sx={{
              width: "100%",
              boxSizing: "border-box",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* бегущая строка */}
            <MarqueeWrapper className="reveal">
              <Marquee speed={50} gradient={false} pauseOnHover>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: "1.8rem", md: "2.2rem" },
                    fontWeight: "bold",
                    fontFamily: "'Bruno Ace SC', sans-serif",
                    color: GLASS.text,
                    mx: 4,
                  }}
                >
                  Stream the Markets. Trade the Predictions.
                </Typography>
              </Marquee>
            </MarqueeWrapper>

            {/* Hero: текст + куб */}
            <SloganBox className="reveal">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9 }}
              >
                <HeroGrid>
                  {/* LEFT */}
                  <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                    <SloganTitle
                      variant="h3"
                      sx={{
                        fontFamily: "'Bruno Ace SC', sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: "2.1rem", md: "2.6rem" },
                        lineHeight: 1.1,
                        mb: 2,
                      }}
                    >
                      AI Streams & Prediction Markets in one app.
                    </SloganTitle>

                    <Typography
                      variant="body1"
                      sx={{
                        color: GLASS.subtext,
                        fontSize: { xs: "1.02rem", md: "1.1rem" },
                        maxWidth: 520,
                        mx: { xs: "auto", md: 0 },
                      }}
                    >
                      Watch AI-driven market streams with our virtual hosts and
                      trade on their predictions through decentralized prediction
                      markets.
                    </Typography>

                    <FeatureList sx={{ mt: 3 }}>
                      <li>
                        <span>
                          Live AI streams & podcasts with real-time market
                          breakdowns
                        </span>
                      </li>
                      <li>
                        <span>
                          Prediction markets on crypto, stocks & macro events
                        </span>
                      </li>
                      <li>
                        <span>
                          Monetize your analysis with tips, boosts and on-chain
                          rewards
                        </span>
                      </li>
                      <li>
                        <span>
                          Promote our AI presenters and get rewards
                        </span>
                      </li>
                    </FeatureList>

                    <Box
                      sx={{
                        mt: 4,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        justifyContent: {
                          xs: "center",
                          md: "flex-start",
                        },
                      }}
                    >
                      <AnimatedButton
                        onClick={handleLaunchApp}
                        endIcon={<OpenInNewIcon />}
                      >
                        Launch App
                      </AnimatedButton>

                      <Button
                        variant="outlined"
                        component={Link}
                        to="/features"
                        sx={{
                          borderRadius: 2,
                          borderColor: "rgba(148,163,184,.6)",
                          color: GLASS.text,
                          textTransform: "none",
                          fontWeight: 600,
                          px: 2.4,
                          "&:hover": {
                            borderColor: NEON.blueSoft,
                            background: "rgba(15,23,42,.9)",
                          },
                        }}
                      >
                         More
                      </Button>
                    </Box>
                  </Box>

                  {/* RIGHT — куб с видео */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <VideoCube previews={videoPreviews} />
                  </Box>
                </HeroGrid>
              </motion.div>
            </SloganBox>
          </Container>

          {/* Ecosystem block (Streams + Prediction Markets) */}
          <Container
            maxWidth="xl"
            className="reveal"
            sx={{
              mt: 6,
              mb: 10,
              width: "100%",
              boxSizing: "border-box",
              position: "relative",
              zIndex: 1,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FeatureBlock
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 5,
                  alignItems: "stretch",
                  p: { xs: 4, md: 6 },
                  maxWidth: "100%",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    fontFamily: "'Bruno Ace SC', sans-serif",
                    color: GLASS.text,
                    textAlign: "center",
                    fontSize: { xs: "2rem", md: "2.4rem" },
                  }}
                >
                  Discover the PromosPay Ecosystem
                </Typography>

                <Grid container spacing={4}>
                  {/* Streams & Podcasts */}
                  <Grid item xs={12} md={6}>
                    <ContentPanel
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: GLASS.brandGrad,
                            boxShadow:
                              "0 8px 22px rgba(192,132,252,.35), 0 6px 20px rgba(56,189,248,.35)",
                          }}
                        >
                          <PlayCircleOutlineIcon
                            sx={{ fontSize: 22, color: "#020617" }}
                          />
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: "bold",
                            fontFamily: "'Bruno Ace SC', sans-serif",
                            color: GLASS.text,
                            fontSize: { xs: "1.6rem", md: "1.9rem" },
                          }}
                        >
                           Streams & Podcasts
                        </Typography>
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          color: GLASS.subtext,
                          fontSize: "0.98rem",
                          mb: 1.5,
                        }}
                      >
                        Live shows with AI-generated hosts commenting on crypto,
                        stocks and macro — powered by our ScAi models.
                      </Typography>

                      <Box sx={{ mb: 1.5 }}>
                        <StepBox>
                          <StepIcon>
                            <PersonAddIcon fontSize="small" />
                          </StepIcon>
                          <StepText>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "'Bruno Ace SC', sans-serif",
                                color: GLASS.text,
                                fontSize: "1rem",
                              }}
                            >
                              Join the stream
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: GLASS.subtext,
                                fontSize: "0.9rem",
                              }}
                            >
                              Watch AI hosts break down market moves in real
                              time.
                            </Typography>
                          </StepText>
                        </StepBox>

                        <StepBox>
                          <StepIcon>
                            <MonetizationOnIcon fontSize="small" />
                          </StepIcon>
                          <StepText>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "'Bruno Ace SC', sans-serif",
                                color: GLASS.text,
                                fontSize: "1rem",
                              }}
                            >
                              Monetize attention
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: GLASS.subtext,
                                fontSize: "0.9rem",
                              }}
                            >
                              Tips, boosts and sponsorships are tied directly to
                              stream performance.
                            </Typography>
                          </StepText>
                        </StepBox>

                        <StepBox>
                          <StepIcon>
                            <AccountBalanceWalletIcon fontSize="small" />
                          </StepIcon>
                          <StepText>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "'Bruno Ace SC', sans-serif",
                                color: GLASS.text,
                                fontSize: "1rem",
                              }}
                            >
                              AI-driven portfolios
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: GLASS.subtext,
                                fontSize: "0.9rem",
                              }}
                            >
                              Turn insights from streams into trades and
                              automated strategies.
                            </Typography>
                          </StepText>
                        </StepBox>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{ color: GLASS.subtext }}
                      >
                        Every stream is backed by market data, on-chain metrics
                        and your own community.
                      </Typography>
                    </ContentPanel>
                  </Grid>

                  {/* Prediction Markets */}
                  <Grid item xs={12} md={6}>
                    <ContentPanel
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "radial-gradient(circle at 0% 0%, rgba(192,132,252,.4), transparent 60%), rgba(15,23,42,1)",
                            boxShadow: GLASS.shadow,
                          }}
                        >
                          <BarChart3 sx={{ fontSize: 22, color: "#e5e7eb" }} />
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: "bold",
                            fontFamily: "'Bruno Ace SC', sans-serif",
                            color: GLASS.text,
                            fontSize: { xs: "1.6rem", md: "1.9rem" },
                          }}
                        >
                          Prediction Markets
                        </Typography>
                      </Box>

                      <Typography
                        variant="body1"
                        sx={{
                          color: GLASS.subtext,
                          fontSize: "0.98rem",
                          mb: 1.5,
                        }}
                      >
                        Trade on yes/no markets directly in PromosPay.
                      </Typography>

                      <Box sx={{ mb: 1.5 }}>
                        <StepBox>
                          <StepIcon>
                            <CircleDollarSign size={18} />
                          </StepIcon>
                          <StepText>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "'Bruno Ace SC', sans-serif",
                                color: GLASS.text,
                                fontSize: "1rem",
                              }}
                            >
                              On-chain positions
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: GLASS.subtext,
                                fontSize: "0.9rem",
                              }}
                            >
                              Buy and sell outcome tokens with transparent
                              liquidity and prices.
                            </Typography>
                          </StepText>
                        </StepBox>

                        <StepBox>
                          <StepIcon>
                            <BarChart3 size={18} />
                          </StepIcon>
                          <StepText>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "'Bruno Ace SC', sans-serif",
                                color: GLASS.text,
                                fontSize: "1rem",
                              }}
                            >
                              AI-scored probabilities
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: GLASS.subtext,
                                fontSize: "0.9rem",
                              }}
                            >
                              ScAi models estimate implied odds and highlight
                              mispriced markets.
                            </Typography>
                          </StepText>
                        </StepBox>

                        <StepBox>
                          <StepIcon>
                            <Vote size={18} />
                          </StepIcon>
                          <StepText>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "'Bruno Ace SC', sans-serif",
                                color: GLASS.text,
                                fontSize: "1rem",
                              }}
                            >
                              Social predictions
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: GLASS.subtext,
                                fontSize: "0.9rem",
                              }}
                            >
                              Community sentiment, leaderboards and your call
                              history in one profile.
                            </Typography>
                          </StepText>
                        </StepBox>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{ color: GLASS.subtext }}
                      >
                        From meme coins to macro decisions — monetize your
                        convictions in a neon, exchange-ready UI.
                      </Typography>
                    </ContentPanel>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <AnimatedButton
                    onClick={handleLaunchApp}
                    endIcon={<OpenInNewIcon />}
                  >
                    Launch App
                  </AnimatedButton>

                  <Button
                    variant="outlined"
                    component="a"
                    href="https://docs.promospay.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: 2,
                      borderColor: "rgba(148,163,184,.6)",
                      color: GLASS.text,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2.4,
                      "&:hover": {
                        borderColor: NEON.violetSoft,
                        background: "rgba(15,23,42,.9)",
                      },
                    }}
                  >
                    Read docs
                  </Button>
                </Box>
              </FeatureBlock>
            </motion.div>
          </Container>
        </HeroSection>

       {/* Partners */}
<Container
  maxWidth="xl"
  className="reveal"
  sx={{
    width: "100%",
    boxSizing: "border-box",
    mt: 0.5,        // чтобы не прилипал к Ecosystem
    mb: 4,
  }}
>
  <PartnersSection>
    <Typography
      variant="body2"
      sx={{
        textAlign: "center",
        mb: 2.5, // было 3
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color: GLASS.subtext,
        fontFamily: "'Bruno Ace SC', sans-serif",
        fontSize: "0.78rem",
      }}
    >
      POWERED BY
    </Typography>

    <PartnersRow>
      {partners.map((partner, index) => (
        <PartnerBadge key={index}>
          <PartnerHalo />
          <PartnerCircle>
            <PartnerLogo
              src={partner}
              alt={`Partner ${index + 1}`}
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
          </PartnerCircle>
        </PartnerBadge>
      ))}
    </PartnersRow>
  </PartnersSection>
</Container>



        {/* Product intersection block */}
        <Container
          maxWidth="xl"
          className="reveal"
          sx={{
            ...glassSectionSX,
            py: 10,
            textAlign: "center",
            my: 8,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 6,
              color: GLASS.text,
              fontFamily: "'Bruno Ace SC', sans-serif",
              textTransform: "uppercase",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            We created a product at the intersection of AI and Blockchain
            technology
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 5,
              alignItems: "stretch",
            }}
          >
            {[
              {
                title: "Fast",
                description:
                  "Clients quickly analyze, create and trade using AI and blockchain.",
                stat: "1-2 minutes",
                statLabel: "Average time to analyze an asset",
              },
              {
                title: "Global coverage",
                description:
                  "Investors, traders, and companies around the world analyze markets, trends, and make forecasts using our platform.",
                stat: "20+ countries",
                statLabel: "PAM coverage",
              },
              {
                title: "Direct payments",
                description:
                  "Direct interaction between clients and AI through smart contracts.",
                stat: "Max benefit",
                statLabel: "Real metrics",
              },
              {
                title: "Possibilities",
                description:
                  "Bonuses, partner promos, fast asset analysis and monetization of your knowledge.",
                stat: "7+ functions",
                statLabel: "Constant expansion of functions",
              },
            ].map((feature, index) => (
              <Box key={index} sx={{ height: "100%", display: "flex" }}>
                <Box
                  sx={{
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderRadius: 12,
                    background: GLASS.surface,
                    border: GLASS.border,
                    color: GLASS.text,
                    transition:
                      "transform .25s ease, box-shadow .25s ease, border-color .25s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 18px 40px rgba(2,8,23,.8)",
                      border: GLASS.borderHover,
                    },
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: GLASS.text,
                        fontFamily: "'Bruno Ace SC', sans-serif",
                        fontSize: "1.25rem",
                        mb: 2,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: GLASS.subtext,
                        fontSize: "0.98rem",
                        minHeight: "120px",
                        overflow: "hidden",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        mt: 2,
                        mb: 0.5,
                        fontWeight: "bold",
                        background: GLASS.brandGrad,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontFamily: "'Bruno Ace SC', sans-serif",
                        fontSize: "1.8rem",
                      }}
                    >
                      {feature.stat}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        color: GLASS.subtext,
                      }}
                    >
                      {feature.statLabel}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>

        {/* Roadmap */}
        <Container
          maxWidth="xl"
          className="reveal"
          sx={{
            ...glassSectionSX,
            py: 10,
            textAlign: "center",
            my: 8,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 6,
              color: GLASS.text,
              fontFamily: "'Bruno Ace SC', sans-serif",
              textTransform: "uppercase",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Roadmap
          </Typography>

          {isMdDown ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {stages.map((stage, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                      p: 2.5,
                      borderRadius: 12,
                      background: GLASS.surface,
                      border: GLASS.border,
                      boxShadow: GLASS.shadow,
                      color: GLASS.text,
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: GLASS.brandGrad,
                        boxShadow:
                          "0 8px 22px rgba(192,132,252,.35), 0 6px 20px rgba(56,189,248,.35)",
                        border: "1px solid rgba(255,255,255,.22)",
                      }}
                    >
                      {stage.icon}
                    </Box>

                    <Box sx={{ flex: 1, textAlign: "left" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 800,
                          fontFamily: "'Bruno Ace SC', sans-serif",
                          color: GLASS.text,
                          lineHeight: 1.2,
                        }}
                      >
                        {stage.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: GLASS.subtext, mt: 0.5 }}
                      >
                        {stage.desc}
                      </Typography>

                      {stage.popupDesc && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: GLASS.subtext,
                            opacity: 0.95,
                            display: "block",
                            mt: 0.75,
                          }}
                        >
                          {stage.popupDesc}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </Box>
          ) : (
            <div
              style={{ width: "100%", height: "600px", position: "relative" }}
            >
              <svg
                className="absolute top-0 left-0 w-full h-full z-0"
                viewBox="0 0 1600 600"
                preserveAspectRatio="xMidYMid meet"
              >
                <motion.path
                  d="M 100 150 L 350 150 L 600 150 L 850 150"
                  stroke="url(#gradTop)"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 2.5, ease: "easeInOut" }}
                />
                <motion.path
                  d="M 850 350 L 1100 350 L 1350 350 L 1600 350"
                  stroke="url(#gradBottom)"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{
                    duration: 2.5,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                />
                <defs>
                  <linearGradient
                    id="gradTop"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      style={{ stopColor: NEON.violet, stopOpacity: 1 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: NEON.blue, stopOpacity: 1 }}
                    />
                  </linearGradient>
                  <linearGradient
                    id="gradBottom"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      style={{ stopColor: NEON.blue, stopOpacity: 1 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: NEON.violet, stopOpacity: 1 }}
                    />
                  </linearGradient>
                </defs>
              </svg>

              {[
                { left: "150px", top: "35px" },
                { left: "400px", top: "35px" },
                { left: "650px", top: "35px" },
                { left: "900px", top: "35px" },
                { left: "450px", top: "300px" },
                { left: "700px", top: "300px" },
                { left: "950px", top: "300px" },
                { left: "1200px", top: "300px" },
              ].map((pos, i) => {
                const stage = stages[i];
                return (
                  <div
                    key={i}
                    className="flex flex-col items-center text-center"
                    style={{
                      width: "200px",
                      minWidth: "200px",
                      position: "absolute",
                      ...pos,
                      transform: "translateX(-50%)",
                    }}
                  >
                    <motion.div
                      className="absolute"
                      style={{
                        width: 18,
                        height: 18,
                        background: NEON.blue,
                        border: "3px solid #ffffff",
                        borderRadius: "50%",
                        top: "-70px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        boxShadow:
                          "0 4px 12px rgba(56,189,248,0.6)",
                      }}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: i * 0.2, duration: 0.5 }}
                    />
                    <motion.div
                      onMouseEnter={() => setActiveIndex(i)}
                      onMouseLeave={() => setActiveIndex(null)}
                      variants={hoverVariants}
                      whileHover="hover"
                      className="relative p-4 rounded-full shadow-xl cursor-pointer"
                      style={{
                        background: GLASS.brandGrad,
                        border: "3px solid #ffffff",
                        boxShadow:
                          "0 4px 12px rgba(15,23,42,0.7)",
                        borderRadius: "9999px",
                      }}
                      transition={{ duration: 0.5 }}
                    >
                      {stage.icon}
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background:
                            "radial-gradient(circle, rgba(255,255,255,0.2), transparent)",
                          opacity: activeIndex === i ? 0.8 : 0,
                          transition: "opacity 0.3s",
                        }}
                      />
                    </motion.div>
                    <h3
                      className="text-base sm:text-lg font-bold font-[Bruno_Ace_SC] mt-4"
                      style={{ color: GLASS.text }}
                    >
                      {stage.title}
                    </h3>
                    <p
                      className="text-sm mt-2 leading-snug"
                      style={{ color: GLASS.subtext, maxWidth: "180px" }}
                    >
                      {stage.desc}
                    </p>
                    {activeIndex === i && (
                      <motion.div
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="mt-4 rounded-xl shadow-2xl p-4 w-[220px] z-50"
                        style={{
                          background: GLASS.surface,
                          boxShadow: GLASS.shadow,
                          border: GLASS.border,
                          borderRadius: 12,
                          position: "absolute",
                          top: i < 4 ? "70px" : "130px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          color: GLASS.text,
                        }}
                      >
                        <h3
                          className="text-lg font-bold font-[Bruno_Ace_SC] mb-1"
                          style={{ color: GLASS.text }}
                        >
                          {stage.title}
                        </h3>
                        <p
                          className="text-sm leading-snug"
                          style={{ color: GLASS.subtext }}
                        >
                          {stage.popupDesc}
                        </p>
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Container>

        {/* Our Mission & Values */}
        <Container
          maxWidth="xl"
          className="reveal"
          sx={{
            ...glassSectionSX,
            py: 10,
            textAlign: "center",
            my: 8,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 6,
              color: GLASS.text,
              fontFamily: "'Bruno Ace SC', sans-serif",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Our Mission & Values
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 5,
              alignItems: "stretch",
            }}
          >
            {[
              {
                icon: require("../assets/images/innovation.png"),
                title: "Innovation",
                text: "We combine blockchain and AI technologies to create unique solutions.",
              },
              {
                icon: require("../assets/images/multifunctionality.png"),
                title: "Multifunctionality",
                text: "Key resources and functions in one app to save time.",
              },
              {
                icon: require("../assets/images/accessibility.png"),
                title: "Accessibility",
                text: "We give you the opportunity to show your market analysis competence to the world.",
              },
              {
                icon: require("../assets/images/partnership.png"),
                title: "Partnership",
                text: "Building a strong ecosystem with businesses and communities.",
              },
            ].map((item, index) => (
              <Box key={index} sx={{ height: "100%", display: "flex" }}>
                <Box
                  sx={{
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    bgcolor: GLASS.surface,
                    borderRadius: 12,
                    border: GLASS.border,
                    boxShadow: GLASS.shadow,
                    transition:
                      "transform .25s ease, box-shadow .25s ease, border-color .25s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 18px 40px rgba(2,8,23,.8)",
                      border: GLASS.borderHover,
                    },
                    height: "100%",
                    width: "100%",
                    color: GLASS.text,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.title}
                      style={{
                        width: 150,
                        height: 150,
                        marginBottom: 16,
                        objectFit: "contain",
                        borderRadius: 50,
                        alignSelf: "center",
                        background: "rgba(255,255,255,0.03)",
                        filter: "drop-shadow(0 8px 22px rgba(2,8,23,.8))",
                      }}
                      onError={(e) => {
                        e.currentTarget.style.visibility = "hidden";
                      }}
                    />
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        fontFamily: "'Bruno Ace SC', sans-serif",
                        color: GLASS.text,
                        fontSize: "1.25rem",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: GLASS.subtext, fontSize: "0.98rem" }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>

        {/* FAQ */}
        <Container
          maxWidth="md"
          className="reveal"
          sx={{
            ...glassSectionSX,
            py: 10,
            textAlign: "center",
            my: 8,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 6,
              color: GLASS.text,
              fontFamily: "'Bruno Ace SC', sans-serif",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Box
            sx={{ textAlign: "left", maxWidth: 900, margin: "0 auto" }}
          >
            {[
              {
                question: "How to use the PromosPay platform?",
                answer:
                  "Connect your wallet and start interacting with our AI, traiding predictions or joining partner promotions. Use trading AI for quick asset analysis or staking for additional yield.",
              },
              {
                question: "What blockchain does PromosPay use?",
                answer:
                  "Our primary network is Ethereum, with plans to expand to BSC and Solana.",
              },
              {
                question:
                  "What is the use of your platform and what can I get here?",
                answer:
                  "We provide you with rapid analysis of assets, projects, and markets using AI, the ability to monetize your analytical expertise, and access to unique products that generate revenue in the market..",
              },
              {
                question:
                  "Why trade predictions, what is your mission??",
                answer:
                  "Along with open and partner data we collect users’ analytics and trading behaviour to train our ScAi models and build a powerful market analysis engine.",
              },
            ].map((faq, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  borderBottom: GLASS.border,
                  pb: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    color: GLASS.text,
                    fontSize: "1.25rem",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                </Typography>
                <AnimatePresence>
                  {openFAQ === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "1.05rem",
                          color: GLASS.subtext,
                        }}
                      >
                        {faq.answer}
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            ))}
          </Box>
          <AnimatedButton
            component="a"
            href="https://x.com/BlockiRoad"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 5, px: 5, py: 2, fontSize: "1.1rem" }}
          >
            More Questions?
          </AnimatedButton>
        </Container>
      </Box>
    </div>
  );
};

export default memo(Home);
